import { WatchQueryFetchPolicy } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshControl } from 'react-native';

import {
  Project,
  Task,
  TaskSortOption,
  TaskStatus,
  Team,
  User,
  useListTasksQuery,
  TaskGroup,
  TaskPriority,
  Scalars,
} from '@graphql/generated';
import { useGetUserAvatarsByIdsQuery } from '@hooks/useGetUserAvatarsByIdsQuery';
import { usePollAtLoadedDepth } from '@hooks/usePollAtLoadedDepth';
import {
  ProjectUpdatedRefreshMode,
  useRefreshOnProjectUpdated,
} from '@hooks/useRefreshOnProjectUpdated';
import {
  logError,
  LogGraphError,
  LogNetworkError,
} from '@utils/logNonFatalError';

/**
 * Retrieves a list of tasks
 */

/**
 * Standard page size for task pagination across all components.
 * This value is chosen to balance performance and UX:
 * - Large enough to reduce number of requests for users with many tasks
 * - Small enough to ensure fast initial load times
 * - Consistent with the chats pagination pattern (PAGE_SIZE = 20)
 */
export const TASK_PAGE_SIZE = 25;

export const useListTasksFromQuery = ({
  userId = undefined,
  projectIds = undefined,
  sortBy = undefined,
  status = undefined,
  first = TASK_PAGE_SIZE,
  last = undefined,
  after = undefined,
  before = undefined,
  fetchPolicy = 'cache-first',
  withoutPhase = false,
  withoutDueDate = undefined,
  onCompleted = undefined,
  onError = undefined,
  skip = false,
  memberIds = undefined,
  teamIds = undefined,
  excludeCompleted = false,
  collaboratorsOnly = false,
  taskCollaborators = undefined,
  dueGroup = undefined,
  priority = undefined,
  dueDateBegin,
  dueDateEnd,
  pollInterval = undefined,
  refreshOnProjectUpdated = undefined,
}: {
  userId?: string;
  memberIds?: User['id'][];
  teamIds?: Team['id'][];
  projectIds?: Project['id'][];
  sortBy?: TaskSortOption;
  status?: TaskStatus;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
  withoutPhase?: boolean;
  withoutDueDate?: boolean;
  onCompleted?: () => void;
  onError?: () => void;
  skip?: boolean;
  excludeCompleted?: boolean;
  collaboratorsOnly?: boolean;
  taskCollaborators?: boolean;
  dueGroup?: TaskGroup;
  priority?: TaskPriority[];
  dueDateBegin?: Scalars['ISO8601DateTime']['input'];
  dueDateEnd?: Scalars['ISO8601DateTime']['input'];
  /** Safety-net poll for callers that need to stay live (My Tasks); the
   * main refresh is refreshOnProjectUpdated (TA-11139). */
  pollInterval?: number;
  /** TA-11139 — see useRefreshOnProjectUpdated. */
  refreshOnProjectUpdated?: ProjectUpdatedRefreshMode;
}) => {
  const [refreshing, setRefreshing] = useState(false);

  const { data, loading, error, refetch, fetchMore, observable } =
    useListTasksQuery({
      variables: {
        userId,
        projectIds,
        sortBy,
        status,
        first,
        last,
        before,
        after,
        memberIds,
        teamIds,
        withoutPhase,
        withoutDueDate,
        excludeCompleted,
        collaboratorsOnly,
        taskCollaborators,
        dueGroup,
        priority,
        dueDateBegin,
        dueDateEnd,
      },
      fetchPolicy,
      skip,
    });
  // Not Apollo's pollInterval: that re-runs page 1 only and drops every page
  // fetchMore added — e.g. a web Tasks section (TA-11016).
  usePollAtLoadedDepth(observable, pollInterval, skip);
  useRefreshOnProjectUpdated(observable, refreshOnProjectUpdated, skip);

  // Use refs so effects always call the latest callback without needing them
  // in the dependency array (avoids stale closures when callers pass inline fns).
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (data) onCompletedRef.current?.();
  }, [data]);
  useEffect(() => {
    if (!error) return;
    if (error.networkError) {
      const event: LogNetworkError = {
        message: 'List Tasks Network Failure',
        errorMessage: error.message,
        statusCode:
          'statusCode' in error.networkError
            ? error.networkError.statusCode
            : -1,
        extra: [{ projectIds }],
      };
      logError(event);
    }
    error.graphQLErrors.forEach((graphError) => {
      const event: LogGraphError = {
        message: 'List Tasks GraphQL Error',
        errorMessage: graphError.message,
        operationName: 'listTasks',
        extra: [{ projectIds }],
      };
      logError(event);
    });
    onErrorRef.current?.();
  }, [error, projectIds]);

  const { checkUserAvatarURLByIds } = useGetUserAvatarsByIdsQuery();
  // Ref so the effect always calls the latest instance without depending on it
  // (the hook returns a new function each render). Mirrors onCompletedRef above.
  const checkUserAvatarURLByIdsRef = useRef(checkUserAvatarURLByIds);
  checkUserAvatarURLByIdsRef.current = checkUserAvatarURLByIds;

  // Stable primitive derived from the actual set of member avatar IDs. Keying
  // the effect on edges?.length missed list changes that keep the count the
  // same (e.g. a member is replaced); a sorted, deduplicated join fires the
  // check whenever the set of IDs truly changes. (TA-9793)
  const taskAvatarIdsKey = useMemo(() => {
    const avatarIds = new Set<string>();
    data?.listTasks?.edges?.forEach((p) => {
      p?.node?.members?.forEach((m) => {
        if (m.user?.avatar?.id) {
          avatarIds.add(m.user.avatar.id);
        }
      });
    });
    return [...avatarIds].sort((a, b) => a.localeCompare(b)).join(',');
  }, [data?.listTasks?.edges]);

  useEffect(() => {
    if (!taskAvatarIdsKey) return;
    checkUserAvatarURLByIdsRef.current(taskAvatarIdsKey.split(','));
  }, [taskAvatarIdsKey]);

  const { listTasks } = data || { listTasks: undefined };
  const { edges = [], pageInfo } = listTasks || { edges: [] };

  // Memoize based on edges reference so downstream consumers (e.g. useMemo
  // in Tasks.web.tsx) only re-run when task content actually changes, not on
  // every render.
  // TODO: This needs to be more properly typed to the returned graphql value.
  // pagination returns duplicate tasks when tasks are named the same resulting in duplicate key errors
  // can be removed when pagination is fixed
  const filterOutDuplicates = useMemo(() => {
    const seenIds = new Set<string>();
    const tasks: Task[] = [];
    edges?.forEach((curr) => {
      if (curr?.node === undefined) return;
      const task = curr.node as Task;
      const taskId = task.id.toString();
      if (seenIds.has(taskId)) return;
      seenIds.add(taskId);
      tasks.push(task);
    });
    return tasks;
  }, [edges]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    refetch().finally(() => setRefreshing(false));
  }, [refetch, setRefreshing]);

  const refreshControl = useMemo(
    () => <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />,
    [refreshing, onRefresh]
  );

  const fetchFromCursor = useCallback(() => {
    if (!filterOutDuplicates || !pageInfo?.hasNextPage || loading) return;

    const cursor = pageInfo.endCursor;
    fetchMore({
      variables: {
        after: cursor,
        first,
      },
    });
  }, [filterOutDuplicates, pageInfo, loading, fetchMore, first]);

  const fetchFromCursorAsync = useCallback(async () => {
    if (!filterOutDuplicates || !pageInfo?.hasNextPage || loading) return;

    const cursor = pageInfo.endCursor;
    return fetchMore({
      variables: {
        after: cursor,
        first,
      },
    });
  }, [filterOutDuplicates, pageInfo, loading, fetchMore, first]);

  return {
    tasks: filterOutDuplicates,
    loading,
    error,
    pageInfo,
    refetch,
    fetchMore,
    fetchFromCursor,
    fetchFromCursorAsync,
    refreshing,
    setRefreshing,
    onRefresh,
    refreshControl,
  };
};
